import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';
import React, {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  SyntheticEvent,
  useMemo,
} from 'react';
import { createGlobalStyle } from 'styled-components';

import {
  Header,
  ModalContainer,
  Tabs,
} from '@core/dashboard/components/PopoverModal';

import SwipeableDrawer from '@core/dashboard/components/SwipeableDrawer';

import ArrowButton from '@core/chat/components/ArrowButton';

import { IChatDrawerView } from './interfaces';

import { useStyles } from './styles';
import { ThirdPartyChatProvider } from '@core/chat/interfaces';
import LoadingProgress from '@components/LoadingProgress';
import { useChatState } from '@core/chat';
import {
  useClientSpecificBroadcastChatVisibility,
  useClientSpecificPrivateChatVisibility,
} from '@hooks/clientSpecificUtils';
import { env } from 'env';

const StreamChat = lazy(
  () => import('@core/chat/StreamChat/components/StreamChat'),
);

// SwipeableDrawer doesn't have sx prop implemented
// so we use global style to override it and add 20px margin
// to prevent scrollbar being stuck behind SwipeableDrawer on desktop
const GlobalStyle = createGlobalStyle<{ $addMarginRight: boolean }>`
  .PrivateSwipeArea-root {
    &:nth-child(2) {
      margin-right: ${(props) => (props.$addMarginRight ? '20px' : '0')};
    }
  }
`;

import { IChatTabs } from '../ChatTabs/interfaces';
import { ChatTabs } from '@core/chat/interfaces';

const ChatDrawerView = ({
  isOpenDrawer,
  toggleDrawer,
  isLoading,
  hasScreenMediaStreams,
  isExpanded,
  setIsExpanded,
  autoClose,
}: IChatDrawerView) => {
  const [isHovered, setIsHovered] = useState(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const classes = useStyles({ isLoading, hasScreenMediaStreams, isExpanded });
  const { isBroadcastEnabled } = useClientSpecificBroadcastChatVisibility();
  const { isPrivateChatEnabled } = useClientSpecificPrivateChatVisibility();

  useEffect(() => {
    const waitTime = parseFloat(
      env.REACT_APP_CHAT_AUTO_CLOSE_TIMEOUT_SECONDS || '120',
    );

    if (!isHovered && timeoutIdRef.current === null) {
      timeoutIdRef.current = setTimeout(() => {
        autoClose();
        timeoutIdRef.current = null;
      }, waitTime * 1000);
    }

    if (isHovered && timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [isHovered, autoClose]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const { t: translate } = useTranslation();

  const handleChangeTab = (_: SyntheticEvent, value: ChatTabs) => {
    setActiveTab(value);
  };

  const { activeTab, setActiveTab } = useChatState();

  const renderChat = () => {
    switch (env.REACT_APP_FEATURE_THIRD_PARTY_CHAT_PROVIDER) {
      case ThirdPartyChatProvider.StreamChat:
        return (
          <Suspense>
            <StreamChat />
          </Suspense>
        );
      default:
        return (
          <Suspense>
            <StreamChat />
          </Suspense>
        );
    }
  };

  const tabs = useMemo(() => {
    const activeTabs = [
      {
        label: translate('chatDrawer.public'),
        value: ChatTabs.Global,
      },
    ];

    if (isPrivateChatEnabled) {
      activeTabs.push({
        label: translate('chatDrawer.private'),
        value: ChatTabs.Private,
      });
    }

    if (isBroadcastEnabled) {
      activeTabs.push({
        label: translate('chatDrawer.broadcast'),
        value: ChatTabs.Broadcast,
      });
    }

    return activeTabs;
  }, [isPrivateChatEnabled, isBroadcastEnabled]);

  return (
    <>
      {/*<GlobalStyle $addMarginRight={isDesktop} />*/}
      <SwipeableDrawer
        position={{
          vertical: isMobileOnly ? 'top' : 'bottom',
          horizontal: 'right',
        }}
        //anchor="right"
        open={isOpenDrawer}
        onClose={toggleDrawer}
        onOpen={toggleDrawer}
        // hideBackdrop
        // disableBackdropTransition
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ModalContainer
          isScrollable={false}
          header={
            <>
              <Header
                text={translate('chatDrawer.title')}
                onClose={toggleDrawer as () => void}
                isBig
              />
              <Tabs
                active={activeTab}
                onChange={handleChangeTab as () => void}
                tabs={tabs}
                sx={classes.tabs}
              />
            </>
          }
          sx={classes.modalContainer}
        >
          <Box sx={classes.box}>
            {renderChat()}
            {/*
            
            <Box sx={classes.collapseWrap}>
              <ArrowButton
                onClick={() => setIsExpanded(!isExpanded)}
                forwardIcon={isExpanded}
                sx={classes.collapse}
              />
            </Box>
          </Box>*/}
          </Box>
        </ModalContainer>
      </SwipeableDrawer>
    </>
  );
};

export default ChatDrawerView;
